import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORY_LABELS, catLabel, fmtDate, hexA } from "./docConstants";
import { StatusBadge, VisibilityChips } from "./DocBits";
import RemindNowDialog from "./RemindNowDialog";
import LastRemindedChip from "./LastRemindedChip";
import BulkRemindDialog from "./BulkRemindDialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/context/AuthContext";

// Focus options map to review-queue sections AND the bulk-remind filter that keeps preview == queue.
const FOCUS = {
  all: {
    label: "All open items", bulkFilter: {},
    groups: [
      { key: "waiting", title: "Waiting for review", icon: "ClipboardCheck", hex: "#B7791F", params: { section: "needs_review" } },
      { key: "clarification", title: "Needs clarification", icon: "HelpCircle", hex: "#9C1E22", params: { status: "needs_clarification" } },
      { key: "signing", title: "Signing pending", icon: "PenLine", hex: "#7C3AED", params: { section: "signing_pending" } },
    ],
  },
  signing_pending: { label: "Signing pending", bulkFilter: { section: "signing_pending" }, groups: [{ key: "signing", title: "Signing pending", icon: "PenLine", hex: "#7C3AED", params: { section: "signing_pending" } }] },
  professional_review: { label: "Professional reviews", bulkFilter: { section: "professional_review" }, groups: [{ key: "professional", title: "Professional reviews", icon: "Scale", hex: "#7C3AED", params: { section: "professional_review" } }] },
  requested: { label: "Requested uploads", bulkFilter: { section: "requested" }, groups: [{ key: "requested", title: "Requested uploads", icon: "FileClock", hex: "#2C6480", params: { section: "requested" } }] },
  shared_externally: { label: "Shared externally", bulkFilter: { section: "shared_externally" }, groups: [{ key: "external", title: "Shared externally", icon: "Building2", hex: "#2C6480", params: { section: "shared_externally" } }] },
  needs_review: { label: "Waiting for review", bulkFilter: { section: "needs_review" }, groups: [{ key: "waiting", title: "Waiting for review", icon: "ClipboardCheck", hex: "#B7791F", params: { section: "needs_review" } }] },
  clarification: { label: "Needs clarification", bulkFilter: { status: "needs_clarification" }, groups: [{ key: "clarification", title: "Needs clarification", icon: "HelpCircle", hex: "#9C1E22", params: { status: "needs_clarification" } }] },
};

// PROMPT 13K — one-click presets that set the Focus/Category filters (+ optional client-side reminder-state view).
const PRESETS = [
  { key: "stale_signing", label: "Stale signing", focus: "signing_pending" },
  { key: "stale_client", label: "Stale client uploads", focus: "requested" },
  { key: "stale_pro", label: "Stale professional reviews", focus: "professional_review" },
  { key: "stale_vendor", label: "Stale vendor / partner uploads", focus: "shared_externally" },
  { key: "clarification", label: "Needs clarification", focus: "clarification" },
  { key: "waiting", label: "Waiting for review", focus: "needs_review" },
  { key: "recently", label: "Recently reminded", focus: "all", remindedState: "recently" },
  { key: "never", label: "Never reminded", focus: "all", remindedState: "never" },
];

const Row = ({ d, onReview, onClarify, onApprove, busy, reminder, onRemind }) => {
  const navigate = useNavigate();
  return (
    <div data-testid={`review-row-${d.id}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#EEF2F5] last:border-0 hover:bg-[#F7FAFC]">
      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`/documents/${d.id}`)}>
        <div className="flex items-center gap-2">
          <Icons.FileText size={14} className="text-[#1C4358] shrink-0" />
          <span className="text-[13.5px] font-semibold text-[#252221] truncate">{d.title}</span>
          <LastRemindedChip reminder={reminder} docId={d.id} />
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-[#6B7280]">
          <span>{catLabel(d.category)}</span>
          {d.uploaded_by_name && <span>· by {d.uploaded_by_name}</span>}
          <span>· {fmtDate(d.updated_at)}</span>
          <VisibilityChips visibility={d.visibility} internalOnly={d.internal_only} />
        </div>
      </div>
      <StatusBadge status={d.status} />
      <div className="flex items-center gap-1">
        <RemindNowDialog docId={d.id} variant="ghost" size="sm" iconOnly className="h-8 text-[#7C3AED]" onSent={onRemind} />
        <Button size="sm" variant="ghost" className="h-8 text-[12px] text-[#2F7D5A]" data-testid={`review-approve-${d.id}`} disabled={busy} onClick={() => onApprove(d)} title="Mark reviewed"><Icons.CheckCircle2 size={15} /></Button>
        <Button size="sm" variant="ghost" className="h-8 text-[12px] text-[#9C1E22]" data-testid={`review-clarify-${d.id}`} disabled={busy} onClick={() => onClarify(d)} title="Needs clarification"><Icons.HelpCircle size={15} /></Button>
        <Button size="sm" variant="ghost" className="h-8 text-[12px] text-[#1C4358]" data-testid={`review-open-${d.id}`} onClick={() => onReview(d)}>Open</Button>
      </div>
    </div>
  );
};

export default function DocumentReview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [focus, setFocus] = useState("all");
  const [category, setCategory] = useState("all");
  const [preset, setPreset] = useState(null);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reminders, setReminders] = useState({});
  const [counts, setCounts] = useState({});
  const [thresholds, setThresholds] = useState({ warning_days: 7, urgent_days: 14 });
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const groups = FOCUS[focus].groups;
  const catFilter = category !== "all" ? { category } : {};
  const bulkFilters = { ...FOCUS[focus].bulkFilter, ...catFilter };
  const remindedState = PRESETS.find((p) => p.key === preset)?.remindedState || null;

  const applyPreset = (p) => { setPreset(p.key); setFocus(p.focus); setCategory("all"); };
  const setFocusManual = (v) => { setPreset(null); setFocus(v); };
  const setCategoryManual = (v) => { setPreset(null); setCategory(v); };
  const clearAll = () => { setPreset(null); setFocus("all"); setCategory("all"); };

  const matchesReminded = useCallback((d) => {
    if (!remindedState) return true;
    const r = reminders[d.id];
    if (remindedState === "never") return !r || !r.last_at;
    if (remindedState === "recently") return !!(r && r.last_at && (Date.now() - new Date(r.last_at).getTime()) < 3 * 86400000);
    return true;
  }, [remindedState, reminders]);

  const loadReminders = useCallback(async (docs) => {
    const ids = docs.map((d) => d.id);
    if (!ids.length) { setReminders({}); return; }
    try { const { data } = await api.post("/doc-center/documents/reminders-summary", { doc_ids: ids }); setReminders(data.summary || {}); }
    catch { /* chip is non-critical */ }
  }, []);

  const loadCounts = useCallback(async () => {
    try { const { data } = await api.get("/doc-center/preset-counts"); setCounts(data.counts || {}); if (data.thresholds) setThresholds(data.thresholds); }
    catch { /* badges are non-critical — fail silently */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const gs = FOCUS[focus].groups;
      const cat = category !== "all" ? { category } : {};
      const res = await Promise.all(gs.map((g) => api.get("/doc-center/documents", { params: { ...g.params, ...cat, limit: 200 } })));
      const grouped = {};
      gs.forEach((g, i) => { grouped[g.key] = res[i].data.documents; });
      setData(grouped);
      loadReminders(Object.values(grouped).flat());
      loadCounts();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [focus, category, loadReminders, loadCounts]);
  useEffect(() => { load(); }, [load]);

  // Apply a preset from the URL (?preset=...) — e.g. from the top-bar triage menu (13M).
  useEffect(() => {
    const pk = searchParams.get("preset");
    const p = PRESETS.find((x) => x.key === pk);
    if (p) { setPreset(p.key); setFocus(p.focus); setCategory("all"); }
  }, [searchParams]);

  const act = async (d, payload, msg) => {
    setBusy(true);
    try { await api.put(`/doc-center/documents/${d.id}`, payload); toast.success(msg); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const allDocs = Object.values(data).flat().filter(matchesReminded);
  const total = allDocs.length;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="pb-12" data-testid="document-review-page">
        <div className="mb-4 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <button onClick={() => navigate("/documents")} className="text-[12px] text-[#1C4358] hover:underline flex items-center gap-1 mb-1"><Icons.ChevronLeft size={13} /> Document Center</button>
            <h1 className="font-display text-2xl font-bold text-[#1C4358]">Review Queue</h1>
            <p className="text-[13px] text-[#6B7280]">Documents that need your attention — {total} item{total !== 1 ? "s" : ""}.</p>
          </div>
          <BulkRemindDialog filters={bulkFilters} onDone={load} />
        </div>

        {/* Quick presets (13K) */}
        <div className="mb-3 flex items-center gap-1.5 flex-wrap" data-testid="review-presets">
          <span className="text-[12px] text-[#6B7280] font-medium mr-0.5">Quick views:</span>
          {PRESETS.map((p) => (
            <button key={p.key} data-testid={`review-preset-${p.key}`} onClick={() => applyPreset(p)}
              className={`text-[12px] rounded-full px-3 py-1 font-medium border transition-colors flex items-center gap-1.5 ${preset === p.key ? "bg-[#1C4358] text-white border-[#1C4358]" : "bg-white text-[#1C4358] border-[#D6E0E7] hover:bg-[#F4F8FB]"}`}>
              {p.label}
              {counts[p.key] != null && (
                <span data-testid={`review-preset-count-${p.key}`} className={`text-[11px] font-semibold rounded-full px-1.5 min-w-[18px] text-center ${preset === p.key ? "bg-white/25 text-white" : counts[p.key] > 0 ? "bg-[#1C4358]/10 text-[#1C4358]" : "bg-[#EEF2F5] text-[#94A3B8]"}`}>{counts[p.key]}</span>
              )}
            </button>
          ))}

          {/* Urgency legend (13Q) — reflects configured thresholds from preset-counts */}
          <span
            data-testid="urgency-legend"
            title={`Document age colors are based on the oldest waiting item. Amber = ${thresholds.warning_days}+ days, Red = ${thresholds.urgent_days}+ days.`}
            className="ml-auto inline-flex items-center gap-2 text-[11.5px] text-[#6B7280] bg-[#F4F8FB] border border-[#E6EDF2] rounded-full px-2.5 py-1"
          >
            <span className="font-medium text-[#4B5563]">Urgency</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#B45309]" /><span data-testid="urgency-legend-warning">{thresholds.warning_days}d</span></span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#9C1E22]" /><span data-testid="urgency-legend-urgent">{thresholds.urgent_days}d</span></span>
            {isSuper && (
              <button data-testid="urgency-legend-edit" onClick={() => navigate("/settings/documents")}
                className="inline-flex items-center gap-0.5 text-[#1C4358] hover:underline font-medium">
                <Icons.Settings2 size={11} /> Edit
              </button>
            )}
          </span>
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="review-filters">
          <Icons.SlidersHorizontal size={15} className="text-[#6B7280]" />
          <Select value={focus} onValueChange={setFocusManual}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="review-filter-focus"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(FOCUS).map(([v, f]) => <SelectItem key={v} value={v}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategoryManual}>
            <SelectTrigger className="h-9 w-[210px]" data-testid="review-filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {(focus !== "all" || category !== "all" || preset) && (
            <Button variant="ghost" size="sm" className="h-9 text-[12px] text-[#6B7280]" data-testid="review-filter-clear" onClick={clearAll}>
              <Icons.X size={13} className="mr-1" /> Clear
            </Button>
          )}
        </div>

        {loading ? <div className="p-10 text-center text-[#6B7280] text-sm"><Icons.Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
          : total === 0 ? <div className="rounded-xl border border-[#E6EDF2] bg-white p-10 text-center text-[#6B7280] text-sm" data-testid="review-empty"><Icons.CheckCircle2 size={22} className="inline mb-1 text-[#2F7D5A]" /><br />All caught up — nothing matches these filters right now.</div>
          : groups.map((g) => {
            const items = (data[g.key] || []).filter(matchesReminded);
            if (!items.length) return null;
            return (
              <div key={g.key} className="mb-5" data-testid={`review-group-${g.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: hexA(g.hex, 0.14) }}>{React.createElement(Icons[g.icon], { size: 15, style: { color: g.hex } })}</div>
                  <h2 className="text-[14px] font-bold text-[#252221]">{g.title}</h2>
                  <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: hexA(g.hex, 0.12), color: g.hex }}>{items.length}</span>
                </div>
                <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden">
                  {items.map((d) => (
                    <Row key={d.id} d={d} busy={busy} reminder={reminders[d.id]}
                      onRemind={() => { loadReminders(allDocs); loadCounts(); }}
                      onReview={(x) => navigate(`/documents/${x.id}`)}
                      onApprove={(x) => act(x, { status: "approved" }, "Marked reviewed.")}
                      onClarify={(x) => act(x, { status: "needs_clarification" }, "Marked needs clarification.")} />
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    </TooltipProvider>
  );
}
